/* Nome de campo cru -> texto de tela, sem mapa escrito à mão.
 *
 * As quatro seções curadas do Margin Finder cumprem a regra de `comum.tsx` — "nenhum
 * snake_case chega à tela" — com um `Record<string,string>` por agente. A seção
 * genérica não pode ter mapa: ela desenha JSON de um agente que ninguém leu. Então a
 * regra vira função.
 *
 * O que ela NÃO faz: adivinhar semântica. `taxa_pct` vira "taxa (%)" porque `pct` é
 * sufixo de unidade, não porque alguém ensinou o que é taxa. */

/** Palavras que ficam feias em caixa-baixa, ou que sozinhas não dizem nada. */
const SIGLAS: Record<string, string> = {
  uf: "UF", cnpj: "CNPJ", cpf: "CPF", ans: "ANS", idss: "IDSS", rc: "RC",
  gb: "GB", mbps: "Mbps", id: "id", ids: "ids", url: "link", urls: "links",
  brl: "reais", iof: "IOF", cet: "CET",
};

/** Sufixo que vira anotação de unidade entre parênteses. */
const UNIDADE: Record<string, string> = { brl: "R$", pct: "%" };

/** Sufixo que quer dizer "quantos", e por isso vira prefixo em português. */
const CONTAGEM = new Set(["n", "qtd", "count"]);

/** `custo_anual_brl` → "custo anual (R$)"; `propostas_n` → "quantidade de propostas".
 *  O sufixo vira rótulo, nunca some — perder o "(R$)" faria dois campos diferentes
 *  (mensal em reais e mensal em pontos) aparecerem com o mesmo nome. */
export function palavras(chave: string): string {
  const partes = chave.split(/[_\-.]+/).filter(Boolean);
  if (partes.length === 0) return chave;

  const ultima = partes[partes.length - 1];
  const corpo = (chave: string[]) => chave.map((p) => SIGLAS[p] ?? p).join(" ");

  if (partes.length > 1 && CONTAGEM.has(ultima)) {
    return `quantidade de ${corpo(partes.slice(0, -1))}`;
  }
  if (partes.length > 1 && UNIDADE[ultima]) {
    return `${corpo(partes.slice(0, -1))} (${UNIDADE[ultima]})`;
  }
  return corpo(partes);
}

/** Rótulo de campo: caixa-baixa, como `.mf-fact .k` já usa ("operadora de hoje"). */
export const rotuloCampo = palavras;

/** Título de bloco: só a inicial sobe, como "Premissas e ressalvas". */
export const tituloBloco = (chave: string): string =>
  palavras(chave).replace(/^./, (c) => c.toUpperCase());

/* Um valor com cara de enum: minúsculas ligadas por `_`, sem espaço. É estreito de
 * propósito — a prosa do agente ("Troque de plano.") não pode ser tocada, e uma frase
 * qualquer nunca casa com isto. */
const ENUM = /^[a-z0-9]+(?:_[a-z0-9]+)+$/;

/** `dados_insuficientes` → "dados insuficientes". Qualquer outra string passa intacta. */
export const valorEnum = (v: string): string =>
  ENUM.test(v) ? v.replace(/_/g, " ") : v;
